import { Injectable } from '@angular/core';
import {
	FieldMode,
	OperatorValue,
	QueryBuilderGroupConfig,
	QueryBuilderRulesConfig,
	RuleFieldType,
} from '@datachain/ui-sdk/components/query-builder';

import {
	RuleFilterPayloadReq,
	WireColumnRef,
} from '../requesters/subscription/models/subscription.model';

/** Caller-provided column object — only `columnId` is consumed by the adapter. */
export interface ColumnRef {
	columnId: number;
	// [key: string]: unknown;
}

/** Map keyed by the column UUID (matches FieldConfig.id / column_alias). */
export type ColumnMap = Map<string, ColumnRef>;

// ─── Operator translation ────────────────────────────────────────────────────

/**
 * Frontend OperatorValue → wire operator string.
 *
 * Notes:
 * - Boolean True/False/Null/NotNull are not in this map; they are operator
 *   *values* on the frontend (held in PrimaryValue) and are rewritten into
 *   wire operators (equal / is_null / is_not_null) by the boolean special-case
 *   logic below.
 * - Date-specific frontend operators collapse to their generic wire equivalents
 *   (e.g. date.eq → equal). The backend handles date semantics by type.
 */
const FRONTEND_TO_WIRE_OPERATOR: Record<string, string> = {
	[OperatorValue.Equal]: 'equal',
	[OperatorValue.NotEqual]: 'not_equal',
	[OperatorValue.Contains]: 'contains',
	[OperatorValue.NotContains]: 'not_contains',
	[OperatorValue.StartsWith]: 'begins_with',
	[OperatorValue.NotStartsWith]: 'not_begins_with',
	[OperatorValue.EndsWith]: 'ends_with',
	[OperatorValue.NotEndsWith]: 'not_ends_with',
	[OperatorValue.ContainedWithin]: 'in',
	[OperatorValue.NotContainedWithin]: 'not_in',
	[OperatorValue.Less]: 'less',
	[OperatorValue.LessEqual]: 'less_or_equal',
	[OperatorValue.Greater]: 'greater',
	[OperatorValue.GreaterEqual]: 'greater_or_equal',
	[OperatorValue.Between]: 'between',
	[OperatorValue.NotBetween]: 'not_between',
	[OperatorValue.Null]: 'is_null',
	[OperatorValue.NotNull]: 'is_not_null',
	[OperatorValue.Empty]: 'is_empty',
	[OperatorValue.NotEmpty]: 'is_not_empty',
	[OperatorValue.IsNumber]: 'is_not_nan',
	[OperatorValue.NotNumber]: 'is_nan',
	[OperatorValue.AllInList]: 'contains_all',
	[OperatorValue.OnelInList]: 'contains_at_least_one',
	[OperatorValue.NoneInList]: 'not_contains_any',
	[OperatorValue.DateEq]: 'equal',
	[OperatorValue.DateNotEq]: 'not_equal',
	[OperatorValue.DateLess]: 'less',
	[OperatorValue.DateLessEq]: 'less_or_equal',
	[OperatorValue.DateGreater]: 'greater',
	[OperatorValue.DateGreaterEq]: 'greater_or_equal',
	[OperatorValue.DateBetween]: 'between',
	[OperatorValue.DateNotBetween]: 'not_between',
};

/**
 * Wire operator string → frontend OperatorValue.
 *
 * Date-specific frontend operators are not represented here because the wire
 * doesn't distinguish them; the type-aware reverse path (toFrontendRule) picks
 * the date.* variants when type is date/dateTime.
 */
const WIRE_TO_FRONTEND_OPERATOR: Record<string, OperatorValue> = {
	equal: OperatorValue.Equal,
	['not_equal']: OperatorValue.NotEqual,
	contains: OperatorValue.Contains,
	['not_contains']: OperatorValue.NotContains,
	['begins_with']: OperatorValue.StartsWith,
	['not_begins_with']: OperatorValue.NotStartsWith,
	['ends_with']: OperatorValue.EndsWith,
	['not_ends_with']: OperatorValue.NotEndsWith,
	in: OperatorValue.ContainedWithin,
	['not_in']: OperatorValue.NotContainedWithin,
	less: OperatorValue.Less,
	['less_or_equal']: OperatorValue.LessEqual,
	greater: OperatorValue.Greater,
	['greater_or_equal']: OperatorValue.GreaterEqual,
	between: OperatorValue.Between,
	['not_between']: OperatorValue.NotBetween,
	['is_null']: OperatorValue.Null,
	['is_not_null']: OperatorValue.NotNull,
	['is_empty']: OperatorValue.Empty,
	['is_not_empty']: OperatorValue.NotEmpty,
	['is_nan']: OperatorValue.NotNumber,
	['is_not_nan']: OperatorValue.IsNumber,
	['contains_all']: OperatorValue.AllInList,
	['contains_at_least_one']: OperatorValue.OnelInList,
	['not_contains_any']: OperatorValue.NoneInList,
};

/**
 * Front-to-wire validation table: which field types each wire operator may be
 * applied to. Derived from the backend operator dictionary (translated to wire
 * semantics). Used on encode only — if a rule pairs an operator with a type not
 * listed here, we log an error but still emit the rule.
 *
 * Notes:
 * - Keyed on the resolved wire operator, so date/dateTime rules whose frontend
 *   operators collapse to generic wire ops (e.g. DateBetween → 'between') are
 *   validated against the same entry.
 * - Deprecated types 'words' and 'file' are intentionally omitted.
 * - Operators absent from this table are not validated (treated as allowed).
 */
const WIRE_OPERATOR_ALLOWED_TYPES: Record<
	string,
	ReadonlyArray<`${RuleFieldType}`>
> = {
	equal: [
		RuleFieldType.FieldString,
		RuleFieldType.FieldBoolean,
		RuleFieldType.FieldNumber,
		RuleFieldType.FieldDate,
		RuleFieldType.FieldDateTime,
	],
	not_equal: [
		RuleFieldType.FieldString,
		RuleFieldType.FieldBoolean,
		RuleFieldType.FieldNumber,
		RuleFieldType.FieldDate,
		RuleFieldType.FieldDateTime,
	],
	in: [
		RuleFieldType.FieldString,
		RuleFieldType.FieldNumber,
		RuleFieldType.FieldDate,
		RuleFieldType.FieldDateTime,
	],
	not_in: [
		RuleFieldType.FieldString,
		RuleFieldType.FieldNumber,
		RuleFieldType.FieldDate,
		RuleFieldType.FieldDateTime,
	],
	less: [
		RuleFieldType.FieldNumber,
		RuleFieldType.FieldDate,
		RuleFieldType.FieldDateTime,
	],
	less_or_equal: [
		RuleFieldType.FieldNumber,
		RuleFieldType.FieldDate,
		RuleFieldType.FieldDateTime,
	],
	greater: [
		RuleFieldType.FieldNumber,
		RuleFieldType.FieldDate,
		RuleFieldType.FieldDateTime,
	],
	greater_or_equal: [
		RuleFieldType.FieldNumber,
		RuleFieldType.FieldDate,
		RuleFieldType.FieldDateTime,
	],
	between: [
		RuleFieldType.FieldNumber,
		RuleFieldType.FieldDate,
		RuleFieldType.FieldDateTime,
	],
	not_between: [
		RuleFieldType.FieldNumber,
		RuleFieldType.FieldDate,
		RuleFieldType.FieldDateTime,
	],
	begins_with: [RuleFieldType.FieldString],
	not_begins_with: [RuleFieldType.FieldString],
	contains: [RuleFieldType.FieldString],
	contains_at_least_one: [RuleFieldType.FieldString],
	contains_all: [RuleFieldType.FieldString],
	not_contains_any: [RuleFieldType.FieldString],
	not_contains: [RuleFieldType.FieldString],
	ends_with: [RuleFieldType.FieldString],
	not_ends_with: [RuleFieldType.FieldString],
	is_empty: [RuleFieldType.FieldString],
	is_not_empty: [RuleFieldType.FieldString],
	is_null: [
		RuleFieldType.FieldString,
		RuleFieldType.FieldBoolean,
		RuleFieldType.FieldNumber,
		RuleFieldType.FieldDate,
		RuleFieldType.FieldDateTime,
	],
	is_not_null: [
		RuleFieldType.FieldString,
		RuleFieldType.FieldBoolean,
		RuleFieldType.FieldNumber,
		RuleFieldType.FieldDate,
		RuleFieldType.FieldDateTime,
	],
	is_nan: [RuleFieldType.FieldNumber],
	is_not_nan: [RuleFieldType.FieldNumber],
};

/**
 * Wire operators that carry no value — frontend should treat as undefined-value
 * operators. The form represents these with placeholder values; the wire sends
 * `value: []` for these.
 */
const UNDEFINED_VALUE_WIRE_OPERATORS = new Set([
	'is_null',
	'is_not_null',
	'is_empty',
	'is_not_empty',
	'is_nan',
	'is_not_nan',
]);

/**
 * Wire operators that use value_list (multi-input). When emitting these,
 * `value` is omitted entirely and `valueList` carries the payload.
 */
const LIST_WIRE_OPERATORS = new Set([
	'in',
	'not_in',
	'contains_all',
	'contains_at_least_one',
	'not_contains_any',
]);

/**
 * Wire operators that use a two-element value array (range).
 */
const RANGE_WIRE_OPERATORS = new Set(['between', 'not_between']);

// ─── Adapter ──────────────────────────────────────────────────────────────────

// FIXME: should be global adapter. Filter <-> Rule adaptation is not subscription specific
@Injectable({
	providedIn: 'root',
})
export class RuleMapperService {
	// ─── Frontend → Backend (groups) ──────────────────────────────────────────

	/**
	 * Convert a frontend group (and any nested groups/rules) to the wire shape.
	 * The root call from the consumer produces the top-level group payload.
	 */
	public toBackendGroup(
		group: QueryBuilderGroupConfig,
		columns: ColumnMap
	): RuleFilterPayloadReq {
		const wire: RuleFilterPayloadReq = {
			condition: group.condition.toUpperCase(),
			rules: group.rules.map((child) => {
				if (this.isGroupConfig(child)) {
					return this.toBackendGroup(child, columns);
				}
				return this.toBackendRule(child, columns);
			}),
		};
		return wire;
	}

	// ─── Backend → Frontend (groups) ──────────────────────────────────────────

	/**
	 * Convert a wire group (and any nested groups/rules) to the frontend shape.
	 */
	public toFrontendGroup(
		wire: RuleFilterPayloadReq,
		columns: ColumnMap
	): QueryBuilderGroupConfig {
		const reverseMap = this.buildReverseColumnMap(columns);
		return this.toFrontendGroupInternal(wire, reverseMap);
	}

	private toFrontendGroupInternal(
		wire: RuleFilterPayloadReq,
		reverseMap: Map<number, string>
	): QueryBuilderGroupConfig {
		const childrenWire = wire.rules ?? [];
		return {
			condition: (wire.condition ?? 'AND').toLowerCase() as 'and' | 'or',
			rules: childrenWire.map((child) => {
				if (this.isWireGroup(child)) {
					return this.toFrontendGroupInternal(child, reverseMap);
				}
				return this.toFrontendRuleInternal(child, reverseMap);
			}),
		};
	}

	// ─── Frontend → Backend (rules) ───────────────────────────────────────────
	private toBackendRule(
		frontend: QueryBuilderRulesConfig,
		columns: ColumnMap
	): RuleFilterPayloadReq {
		const rule: RuleFilterPayloadReq = {};

		// label → field; id → columnAlias.
		rule.field = frontend.label;
		rule.column_alias = frontend.id;

		// Type is the same on both sides for the values we map; date passthrough.
		rule.type = frontend.type === 'dateTime' ? 'date' : frontend.type;

		// ignoreCase defaults to true when not provided.
		// FIXME: should only apply to string types only
		rule.ign_case = frontend.ignoreCase ?? false;

		// Boolean: rewrite (operator, primaryValue) into wire operator + value.
		if (frontend.type === RuleFieldType.FieldBoolean) {
			this.encodeBooleanRule(frontend, rule);
			return rule;
		}

		// Non-boolean: resolve wire operator from the frontend operator.
		const frontendOp = frontend.operator;
		if (!frontendOp || frontendOp === 'noop') {
			return rule;
		}
		const wireOp = FRONTEND_TO_WIRE_OPERATOR[frontendOp];
		if (wireOp === undefined) {
			console.warn(`No wire mapping for frontend operator "${frontendOp}"`);
			return rule;
		}
		rule.operator = wireOp;

		const allowedTypes = WIRE_OPERATOR_ALLOWED_TYPES[wireOp];
		if (allowedTypes && !allowedTypes.includes(frontend.type)) {
			console.warn(
				`Operator "${wireOp}" is not valid for type "${frontend.type}"`
			);
		}

		// Undefined-value operators: value: [], no column refs.
		if (UNDEFINED_VALUE_WIRE_OPERATORS.has(wireOp)) {
			// rule.value = [];
			return rule;
		}

		// List operators: valueList carries the payload, value is omitted.
		if (LIST_WIRE_OPERATORS.has(wireOp)) {
			rule.value_list = this.encodeListValue(
				frontend.primaryValue,
				frontend.primaryValueMode,
				columns
			);
			return rule;
		}

		// Range operators: value: [primary, secondary], plus optional column refs.
		if (RANGE_WIRE_OPERATORS.has(wireOp)) {
			this.encodeRangeRule(frontend, rule, columns);
			return rule;
		}

		// Single-value operators: entry → value, comparison → valueColumnId.
		if (frontend.primaryValueMode === 'comparison') {
			rule.value_column_id = this.lookupColumnUuid(
				frontend.primaryValue,
				columns
			);
		} else {
			rule.value = this.serializeScalar(frontend.primaryValue, frontend.type);
		}

		return rule;
	}

	// ─── Backend → Frontend (rules) ───────────────────────────────────────────

	private toFrontendRuleInternal(
		backend: RuleFilterPayloadReq,
		reverseMap: Map<number, string>
	): QueryBuilderRulesConfig {
		const type =
			(backend.type as `${RuleFieldType}`) ?? RuleFieldType.FieldString;
		const wireOp = backend.operator ?? '';

		const config: QueryBuilderRulesConfig = {
			id: backend.column_alias ?? '',
			label: backend.field ?? '',
			type: type === 'date' ? 'dateTime' : type,
			primaryValue: '',
			ignoreCase: backend.ign_case ?? true,
		};

		// Boolean
		if (type === RuleFieldType.FieldBoolean) {
			if (backend.value_column_id) {
				config.operator =
					backend.operator === 'not_equal'
						? OperatorValue.NotEqual
						: OperatorValue.Equal;
				config.primaryValue = backend.value_column_id;
				config.primaryValueMode = 'comparison';
			} else {
				// Boolean: rewrite wire (operator, value) → frontend (equals, value).
				this.decodeBooleanRule(backend, config);
			}
			return config;
		}

		// Resolve frontend operator; prefer date.* variants for date types.
		const frontendOp = this.resolveFrontendOperator(wireOp, type);
		if (frontendOp) {
			config.operator = frontendOp;
		}

		// Undefined-value: placeholder primary, no secondary.
		if (UNDEFINED_VALUE_WIRE_OPERATORS.has(wireOp)) {
			config.primaryValue = this.defaultPlaceholderFor(wireOp, type);
			config.primaryValueMode = 'entry';
			return config;
		}

		// List operators: decode valueList to either Array<string> (entry mode)
		// or Array<columnUuid> (comparison mode).
		if (LIST_WIRE_OPERATORS.has(wireOp)) {
			const decoded = this.decodeListValue(
				backend.value_list ?? [],
				reverseMap
			);
			config.primaryValue = decoded.values;
			config.primaryValueMode = decoded.mode;
			return config;
		}

		// Range operators: decode the [p, s] array plus any column refs.
		if (RANGE_WIRE_OPERATORS.has(wireOp)) {
			this.decodeRangeRule(backend, config, reverseMap);
			return config;
		}

		// Single-value: comparison if valueColumnId set, else entry.
		if (backend.value_column_id) {
			config.primaryValue = backend.value_column_id;
			config.primaryValueMode = 'comparison';
		} else {
			config.primaryValue = this.deserializeScalar(
				backend.value as string | undefined,
				type
			);
			config.primaryValueMode = 'entry';
		}

		return config;
	}

	// ─── Boolean special-case ─────────────────────────────────────────────────

	/**
	 * Maps all eight combinations of (Equals/NotEquals × True/False/Null/NotNull)
	 * to the appropriate wire operator + value. Double-negation pairs collapse:
	 * NotEquals + Null is equivalent to Equals + NotNull.
	 */
	private encodeBooleanRule(
		frontend: QueryBuilderRulesConfig,
		rule: RuleFilterPayloadReq
	): void {
		const op = frontend.operator;
		const val = frontend.primaryValue;

		// In comparison mode on a boolean field, the value is a column UUID.
		if (frontend.primaryValueMode === 'comparison') {
			rule.operator = op === OperatorValue.NotEqual ? 'not_equal' : 'equal';
			// Caller passes columns through encodeBooleanRule indirectly — but we
			// didn't carry the map here. Comparison mode on boolean is rare; resolve
			// via a tagged sentinel for now and let toBackendRule re-route if needed.
			// In practice the existing UI puts a column UUID string in primaryValue.
			// Without the map at hand, fall back to passing the UUID through unchanged
			// — this branch should be invoked through the public API which has the map.
			rule.value_column_id = String(val);
			return;
		}

		const isNot = op === OperatorValue.NotEqual;
		if (op === OperatorValue.Null || op === OperatorValue.NotNull) {
			rule.operator = FRONTEND_TO_WIRE_OPERATOR[op];
			return; // undefined-value operator: no value emitted
		}
		switch (val) {
			case OperatorValue.True: // 'true'
				rule.operator = 'equal';
				rule.value = isNot ? '0' : '1';
				return;
			case OperatorValue.False: // 'false'
				rule.operator = 'equal';
				rule.value = isNot ? '1' : '0';
				return;
			default:
				// Unrecognized boolean value — emit equal + raw stringification.
				console.error(`Unexpected boolean primary value: ${String(val)}`);
				rule.operator = isNot ? 'not_equal' : 'equal';
				rule.value = String(val);
		}
	}

	/**
	 * Inverse of encodeBooleanRule: wire (operator, value) → frontend
	 * (equals, primaryValue). Always emits Equals on the way back.
	 */
	private decodeBooleanRule(
		backend: RuleFilterPayloadReq,
		config: QueryBuilderRulesConfig
	): void {
		config.operator = OperatorValue.Equal;
		config.primaryValueMode = 'entry';

		const wireOp = backend.operator;
		if (wireOp === 'is_null') {
			config.operator = OperatorValue.Null;
			return;
		}
		if (wireOp === 'is_not_null') {
			config.operator = OperatorValue.NotNull;
			return;
		}

		const v = backend.value;
		if (v === '1') {
			config.primaryValue = OperatorValue.True;
		} else if (v === '0') {
			config.primaryValue = OperatorValue.False;
		} else {
			config.primaryValue = OperatorValue.True;
		}
	}

	// ─── List (multi) value encoding ──────────────────────────────────────────

	private encodeListValue(
		primary: QueryBuilderRulesConfig['primaryValue'],
		mode: FieldMode | undefined,
		columns: ColumnMap
	): Array<string | number | WireColumnRef> {
		const arr = Array.isArray(primary) ? primary : [];
		if (mode === 'comparison') {
			return arr.map((uuid) => {
				if (typeof uuid === 'string' && !columns.has(uuid)) {
					console.error(`No column found for UUID "${uuid}"`);
				}
				return {
					isColumn: true as const,
					columnId: uuid as string,
				};
			});
		}
		// Entry mode: stringified literal values.
		return arr.map((v) => v);
	}

	private decodeListValue(
		list: Array<string | number | WireColumnRef>,
		reverseMap: Map<number, string>
	): { values: Array<string> | Array<number>; mode: FieldMode } {
		if (list.length === 0) {
			return {
				values: [],
				mode: 'entry',
			};
		}
		const looksLikeColumns = list.every(
			(item) => typeof item === 'object' && item !== null && 'isColumn' in item
		);
		if (looksLikeColumns) {
			const refs = list as Array<WireColumnRef>;
			return {
				values: refs.map((ref) => String(ref.columnId)),
				mode: 'comparison',
			};
		}
		return {
			values: [...list] as Array<string> | Array<number>,
			mode: 'entry',
		};
	}

	// ─── Range encoding ───────────────────────────────────────────────────────

	private encodeRangeRule(
		frontend: QueryBuilderRulesConfig,
		rule: RuleFilterPayloadReq,
		columns: ColumnMap
	): void {
		const isPrimaryComparison = frontend.primaryValueMode === 'comparison';
		const isSecondaryComparison = frontend.secondaryValueMode === 'comparison';

		const placeholder = this.rangePlaceholderFor(frontend.type);

		// value: [primary, secondary] with placeholder "" for comparison sides.
		const primaryAsString = isPrimaryComparison
			? placeholder
			: this.serializeScalar(frontend.primaryValue, frontend.type);
		const secondaryAsString = isSecondaryComparison
			? placeholder
			: this.serializeScalar(frontend.secondaryValue, frontend.type);
		rule.value = JSON.stringify([primaryAsString, secondaryAsString]);

		if (isPrimaryComparison) {
			rule.value_column_id = this.lookupColumnUuid(
				frontend.primaryValue,
				columns
			);
		}
		if (isSecondaryComparison) {
			rule.value_column_id2 = this.lookupColumnUuid(
				frontend.secondaryValue,
				columns
			);
		}
	}

	private rangePlaceholderFor(type: string): string {
		if (
			type === RuleFieldType.FieldDate ||
			type === RuleFieldType.FieldDateTime
		) {
			return '';
		}
		// Numbers (integer, decimal, bigInteger, number).
		return '';
	}

	private decodeRangeRule(
		backend: RuleFilterPayloadReq,
		config: QueryBuilderRulesConfig,
		reverseMap: Map<number, string>
	): void {
		let valueArr: Array<string> = ['', ''];
		if (typeof backend.value === 'string' && backend.value !== '') {
			try {
				const parsed = JSON.parse(backend.value);
				if (Array.isArray(parsed)) {
					valueArr = parsed;
				}
			} catch {
				console.error(`Invalid range value: ${backend.value}`);
			}
		} else if (Array.isArray(backend.value)) {
			valueArr = backend.value;
		}
		const [primaryStr, secondaryStr] = valueArr;

		if (backend.value_column_id) {
			config.primaryValue = backend.value_column_id;
			config.primaryValueMode = 'comparison';
		} else {
			config.primaryValue = this.deserializeScalar(primaryStr, config.type);
			config.primaryValueMode = 'entry';
		}

		if (backend.value_column_id2) {
			config.secondaryValue = backend.value_column_id2;
			config.secondaryValueMode = 'comparison';
		} else {
			config.secondaryValue = this.deserializeScalar(
				secondaryStr,
				config.type
			) as QueryBuilderRulesConfig['secondaryValue'];
			config.secondaryValueMode = 'entry';
		}
	}

	// ─── Operator resolution helpers ──────────────────────────────────────────

	/**
	 * Wire op string → frontend OperatorValue. Picks the date.* variant when
	 * the field type is date/dateTime and a date variant exists for the wire op.
	 */
	private resolveFrontendOperator(
		wireOp: string,
		type: string
	): OperatorValue | undefined {
		const isDateType =
			type === RuleFieldType.FieldDate || type === RuleFieldType.FieldDateTime;

		if (isDateType) {
			const dateVariant: Record<string, OperatorValue> = {
				equal: OperatorValue.DateEq,
				['not_equal']: OperatorValue.DateNotEq,
				less: OperatorValue.DateLess,
				['less_or_equal']: OperatorValue.DateLessEq,
				greater: OperatorValue.DateGreater,
				['greater_or_equal']: OperatorValue.DateGreaterEq,
				between: OperatorValue.DateBetween,
				['not_between']: OperatorValue.DateNotBetween,
			};
			if (wireOp in dateVariant) {
				return dateVariant[wireOp];
			}
		}

		return WIRE_TO_FRONTEND_OPERATOR[wireOp];
	}

	// ─── Column map lookups ───────────────────────────────────────────────────

	private buildReverseColumnMap(columns: ColumnMap): Map<number, string> {
		const reverse = new Map<number, string>();
		columns.forEach((col, uuid) => {
			reverse.set(col.columnId, uuid);
		});
		return reverse;
	}

	private lookupColumnNumericId(uuid: unknown, columns: ColumnMap): number {
		if (typeof uuid !== 'string') {
			console.error(`Column reference value is not a string: ${String(uuid)}`);
			return 0;
		}
		const col = columns.get(uuid);
		if (!col) {
			console.error(`No column found for UUID "${uuid}"`);
			return 0;
		}
		return col.columnId;
	}

	/**
	 * For comparison-mode single-value cases, the wire field is `valueColumnId`
	 * which is the UUID string itself (not the numeric id). The frontend passes
	 * through the same UUID, so this is effectively identity, but we still
	 * validate against the map to surface bad references.
	 */
	private lookupColumnUuid(value: unknown, columns: ColumnMap): string {
		if (typeof value !== 'string') {
			console.error(`Column reference value is not a string: ${String(value)}`);
			return '';
		}
		if (!columns.has(value)) {
			console.error(`No column found for UUID "${value}"`);
		}
		return value;
	}

	// ─── Group / leaf detection ───────────────────────────────────────────────

	private isGroupConfig(
		x: QueryBuilderRulesConfig | QueryBuilderGroupConfig
	): x is QueryBuilderGroupConfig {
		return Array.isArray((x as QueryBuilderGroupConfig).rules);
	}

	private isWireGroup(wire: RuleFilterPayloadReq): boolean {
		return Array.isArray(wire.rules);
	}

	// ─── Value serialization (single scalar) ──────────────────────────────────

	private serializeScalar(value: unknown, type: string | undefined): string {
		if (value === undefined || value === null || value === '') {
			return '';
		}
		if (value instanceof Date) {
			return value.toISOString();
		}
		if (typeof value === 'boolean') {
			return value ? '1' : '0';
		}
		return String(value);
	}

	private deserializeScalar(
		raw: string | undefined,
		type: string
	): boolean | number | string | Date {
		if (raw === undefined || raw === null || raw === '') {
			return '';
		}
		switch (type as RuleFieldType) {
			case RuleFieldType.FieldNumber:
				return Number(raw);
			case RuleFieldType.FieldBoolean:
				return raw === '1';
			case RuleFieldType.FieldDate:
			case RuleFieldType.FieldDateTime: {
				const parsed = new Date(raw);
				if (isNaN(parsed.getTime())) {
					console.error(`Invalid ISO date from wire: "${raw}"`);
					return raw;
				}
				return parsed;
			}
			default:
				return raw;
		}
	}

	/**
	 * Placeholder value for an undefined-value operator on the frontend side,
	 * matching what the rule builder produces for fresh rules.
	 */
	private defaultPlaceholderFor(wireOp: string, type: string): string {
		if (wireOp === 'is_empty' || wireOp === 'is_not_empty') {
			return '';
		}
		// Date types in null/not-null cases hold null in the actual control,
		// but the frontend treats undefined-value operators as having a
		// placeholder; the rule builder uses 'N/A' for these.
		if (
			type === RuleFieldType.FieldDate ||
			type === RuleFieldType.FieldDateTime
		) {
			return '';
		}
		return 'N/A';
	}

	// ─── Date formatting / parsing ────────────────────────────────────────────

	/** Format a Date as `dd/MM/yyyy HH:mm:ss` using local time components. */
	private formatDateTime(d: Date): string {
		const pad = (n: number): string => String(n).padStart(2, '0');
		return (
			`${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ` +
			`${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
		);
	}

	/**
	 * Format a Date as `dd/MM/yyyy 00:00:00`. Currently date and dateTime share
	 * the same wire format; the backend treats `date` as `dateTime` with time
	 * forced to midnight. Once the backend supports a date-only format this
	 * should switch to e.g. `dd/MM/yyyy`.
	 */
	private formatDate(d: Date): string {
		const pad = (n: number): string => String(n).padStart(2, '0');
		return `${pad(d.getDate())}/${pad(
			d.getMonth() + 1
		)}/${d.getFullYear()} 00:00:00`;
	}

	/**
	 * Parse a `dd/MM/yyyy HH:mm:ss` (or `dd/MM/yyyy`) string back to a Date.
	 * Returns null if the string doesn't match.
	 */
	private parseWireDate(raw: string): Date | null {
		const match = raw.match(
			/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}):(\d{2}))?$/
		);
		if (!match) {
			return null;
		}
		const [, dd, mm, yyyy, hh = '0', mi = '0', ss = '0'] = match;
		const d = new Date(
			Number(yyyy),
			Number(mm) - 1,
			Number(dd),
			Number(hh),
			Number(mi),
			Number(ss)
		);
		return isNaN(d.getTime()) ? null : d;
	}
}
